import { type ResolvedSentryConfig, resolveSentryConfig } from "@sophy-ai/chat-core/monitoring/sentry"

type WebSentrySdk = {
  init: (config: Record<string, unknown>) => void
}

type WebSdkLoader = () => Promise<WebSentrySdk>
type WebSentryRuntime = "next-server" | "next-edge" | "next-client"

const defaultWebSdkLoader: WebSdkLoader = () => import("@sentry/nextjs")
const initializedWebSentry = new WeakMap<WebSdkLoader, Map<WebSentryRuntime, Promise<ResolvedSentryConfig>>>()

function toInitConfig(config: ResolvedSentryConfig) {
  return {
    dsn: config.dsn,
    enabled: config.enabled,
    environment: config.environment,
    release: config.release,
    tracesSampleRate: config.tracesSampleRate,
    profilesSampleRate: config.profilesSampleRate,
    debug: config.debug,
    sendDefaultPii: config.sendDefaultPii,
    tunnel: config.tunnelRoute,
  }
}

async function initializeWebSentry(
  env: Record<string, string | undefined>,
  runtime: WebSentryRuntime,
  loadSdk: WebSdkLoader
): Promise<ResolvedSentryConfig> {
  const config = resolveSentryConfig({ env, runtime, serviceName: "web" })
  if (!config.enabled) {
    return config
  }

  const runtimeCache = initializedWebSentry.get(loadSdk) ?? new Map<WebSentryRuntime, Promise<ResolvedSentryConfig>>()
  initializedWebSentry.set(loadSdk, runtimeCache)

  const existing = runtimeCache.get(runtime)
  if (existing) {
    return existing
  }

  const initialization = (async () => {
    try {
      const sentry = await loadSdk()
      sentry.init(toInitConfig(config))
    } catch (error) {
      runtimeCache.delete(runtime)
      console.warn("[web] Sentry init skipped:", error)
    }

    return config
  })()

  runtimeCache.set(runtime, initialization)
  return initialization
}

export function buildNextSentryPluginOptions(env: Record<string, string | undefined>) {
  const config = resolveSentryConfig({ env, runtime: "next-server", serviceName: "web" })
  return {
    org: config.sourceMaps.org,
    project: config.sourceMaps.project,
    authToken: config.sourceMaps.authToken,
    ...(config.release
      ? {
          release: {
            name: config.release,
          },
        }
      : {}),
    tunnelRoute: config.tunnelRoute,
    silent: true,
    disableLogger: true,
    widenClientFileUpload: true,
    sourcemaps: {
      deleteSourcemapsAfterUpload: true,
    },
  }
}

export async function initializeServerSentry(
  env: Record<string, string | undefined> = process.env,
  loadSdk: WebSdkLoader = defaultWebSdkLoader
) {
  return initializeWebSentry(env, "next-server", loadSdk)
}

export async function initializeEdgeSentry(
  env: Record<string, string | undefined> = process.env,
  loadSdk: WebSdkLoader = defaultWebSdkLoader
) {
  return initializeWebSentry(env, "next-edge", loadSdk)
}

export async function initializeClientSentry(
  env: Record<string, string | undefined> = process.env,
  loadSdk: WebSdkLoader = defaultWebSdkLoader
) {
  return initializeWebSentry(env, "next-client", loadSdk)
}
